import React from 'react';
import { Modal } from '../UI/Modal';
import { SlideshowSettingsTab } from './SlideshowSettingsTab';
import type { Album } from '../../types';

interface SlideshowSetupModalProps {
  album: Album | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updated: Album) => void;
}

/**
 * Focused mini-modal that exposes the slideshow setup/config UI without
 * forcing the user through the full multi-tab AlbumEditModal. Used as the
 * canonical "Venue Display" entry point from album cards, the album view
 * page, and other surfaces.
 *
 * The body is intentionally a thin wrapper — all of the visual design and
 * setup flow lives in SlideshowSettingsTab so it stays consistent whether
 * the user reaches it from this modal, from the AlbumEditModal tab, or
 * from the LiveAlbumWizard Done step.
 *
 * Sizing is state-dependent: `md` for the compact setup flow, `full` for the
 * Display Studio workspace once the display exists.
 */
export function SlideshowSetupModal({ album, isOpen, onClose, onUpdated }: SlideshowSetupModalProps) {
  if (!album) return null;

  // Two very different shapes share this entry point: the setup flow is a
  // compact focused task, while the Display Studio is a full workspace.
  const size = album.slideshowEnabled ? 'full' : 'md';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size}>
      <SlideshowSettingsTab album={album} onUpdated={onUpdated} onClose={onClose} />
    </Modal>
  );
}
